import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { File, Image, Music, Video, Trash2, Eye, Download, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function StoragePage() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadFiles(); }, []);

  const loadFiles = async () => {
    try { const res = await api.get('/files'); setFiles(res.data); }
    catch { toast.error('Failed to load files'); }
    finally { setLoading(false); }
  };

  const openPreview = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}/preview`);
      setPreviewData(res.data);
      setPreview(file);
    } catch { toast.error('Failed to preview'); }
  };

  const handleDelete = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      setFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('File deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownload = (file) => {
    if (!previewData?.file_data) return;
    const link = document.createElement('a');
    link.href = `data:${file.file_type};base64,${previewData.file_data}`;
    link.download = file.filename;
    link.click();
  };

  const getIcon = (type) => {
    if (type?.startsWith('image')) return Image;
    if (type?.startsWith('video')) return Video;
    if (type?.startsWith('audio')) return Music;
    return File;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="animate-in" data-testid="storage-page">
      <h1 className="font-serif text-2xl md:text-3xl mb-2">Storage</h1>
      <p className="text-sm text-muted-foreground mb-8">Manage your uploaded files and media</p>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading...</div>
      ) : files.length === 0 ? (
        <div className="text-center py-16 border border-border/40">
          <FolderOpen size={32} className="mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-sm text-muted-foreground">Your storage is empty. Upload files to see them here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {files.map((f, i) => {
            const Icon = getIcon(f.file_type);
            return (
              <div
                key={f.id}
                className="border border-border/40 hover:border-gold/30 transition-colors duration-500 animate-in group"
                style={{ animationDelay: `${i * 40}ms` }}
                data-testid={`storage-file-${f.id}`}
              >
                <div className="aspect-video bg-mist flex items-center justify-center relative">
                  <Icon size={28} className="text-muted-foreground/30" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Button variant="outline" size="sm" className="rounded-sm text-xs bg-white/90 mr-2" onClick={() => openPreview(f)} data-testid={`preview-file-${f.id}`}>
                      <Eye size={12} className="mr-1" /> Preview
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-sm text-xs bg-white/90 text-destructive" onClick={() => handleDelete(f.id)} data-testid={`delete-file-${f.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-xs font-medium truncate">{f.filename}</p>
                  <p className="text-[10px] text-muted-foreground">{formatSize(f.file_size)} &middot; {new Date(f.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => { setPreview(null); setPreviewData(null); }}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif">{preview?.filename}</DialogTitle></DialogHeader>
          <div className="mt-4">
            {previewData?.file_data && preview?.file_type?.startsWith('image') && (
              <img src={`data:${preview.file_type};base64,${previewData.file_data}`} alt={preview.filename} className="w-full max-h-[60vh] object-contain" />
            )}
            {previewData?.file_data && preview?.file_type?.startsWith('video') && (
              <video src={`data:${preview.file_type};base64,${previewData.file_data}`} controls className="w-full max-h-[60vh]" />
            )}
            {previewData?.file_data && preview?.file_type?.startsWith('audio') && (
              <audio src={`data:${preview.file_type};base64,${previewData.file_data}`} controls className="w-full mt-8" />
            )}
            {previewData?.file_data && !preview?.file_type?.startsWith('image') && !preview?.file_type?.startsWith('video') && !preview?.file_type?.startsWith('audio') && (
              <div className="text-center py-8">
                <File size={48} className="mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-sm text-muted-foreground">Preview not available for this file type.</p>
              </div>
            )}
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-border/40">
              <div>
                <p className="text-xs text-muted-foreground">{preview?.file_type} &middot; {formatSize(preview?.file_size || 0)}</p>
              </div>
              <Button data-testid="preview-download-btn" variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => handleDownload(preview)}>
                <Download size={12} className="mr-1" /> Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
